<template>
    <Card class="h-full">
        <CardHeader>
            <div class="flex items-center gap-2">
                <Database class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Custom storage
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent class="flex flex-1 flex-col">
        <div class="mb-4 flex flex-col gap-2">
            <button
                v-for="provider in orderedProviders"
                :key="provider.key"
                class="flex w-full min-w-0 items-center justify-between gap-3 bg-new-neutral-50 px-4 py-2 transition-colors hover:bg-new-neutral-100"
                @click="$emit('selectProvider', provider.key)"
            >
                <p class="min-w-0 text-left text-xs text-muted-foreground">
                    {{ apiToken.settings?.storage_provider ? `Edit ${provider.name} storage` : `Set up ${provider.name} storage` }}
                </p>
                <div class="flex h-10 shrink-0 items-center justify-center">
                    <img
                        :src="provider.img"
                        :alt="provider.name"
                        class="object-contain"
                        :style="figmaSize(provider.key)"
                    />
                </div>
            </button>
        </div>

        <p class="mt-auto max-w-xs text-[10px] min-[1920px]:text-[12px] leading-4 text-muted-foreground">
            <span v-if="!apiToken?.settings?.storage_provider">
                Currently using default storage.<br> Configure your own Google Cloud, Amazon S3, Cloudflare R2, or DigitalOcean Spaces.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'gcs'">
                Using custom Google Cloud storage.<br> Detach current storage to switch providers.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'aws'">
                Using Amazon S3 storage.<br> Detach current storage to switch providers.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'cloudflare'">
                Using Cloudflare R2 storage.<br> Detach current storage to switch providers.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'digitalocean'">
                Using DigitalOcean Spaces storage.<br> Detach current storage to switch providers.
            </span>
            For more information,
            <a
                href="https://docs.topol.io"
                target="_blank"
                class="font-semibold underline text-new-blue-500 hover:text-new-blue-600"
            >
                check out our documentation</a
            >.
        </p>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { PropType, computed } from 'vue';
import { Database } from 'lucide-vue-next';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import IToken from '@/types/Plugin/IToken';
import { allProviders } from '@/Pages/PluginSettings/data/storageProviders';

const props = defineProps({
    apiToken: {
        type: Object as PropType<IToken>,
        required: true,
    },
});

defineEmits<{
    selectProvider: [provider: 'aws' | 'cloudflare' | 'gcs' | 'digitalocean'];
}>();

const visibleProviders = computed(() => {
    const currentProvider = props.apiToken?.settings?.storage_provider;

    if (currentProvider) {
        return allProviders.filter((p) => p.key === currentProvider);
    }

    return allProviders;
});

const providerOrder: Record<
    'cloudflare' | 'gcs' | 'aws' | 'digitalocean',
    number
> = {
    cloudflare: 0,
    gcs: 1,
    aws: 2,
    digitalocean: 3,
};

const orderedProviders = computed(() =>
    [...visibleProviders.value].sort(
        (a, b) => providerOrder[a.key] - providerOrder[b.key]
    )
);

const figmaSize = (key: 'gcs' | 'aws' | 'cloudflare' | 'digitalocean') => {
    switch (key) {
        case 'gcs':
            return { width: '102px', height: '40px' };
        case 'aws':
            return { width: '32px', height: '32px' };
        case 'cloudflare':
            return { width: '73px', height: '24px' };
        case 'digitalocean':
            return { width: '102px', height: '40px' };
    }
};
</script>
